import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { bulkUpload } from "@api/bulkApi";
import type {
  BulkClientAlertType,
  BulkClientUploadFileError,
  BulkClientUploadRecordError,
  BulkClientUploadResponse,
} from "./types";

const NO_FILE_SELECTED_MESSAGE = "No CSV file selected.";
const UPLOADING_TITLE = "Uploading clients...";
const UPLOADING_MESSAGE =
  "Please don't refresh or close the page while the upload is in progress.";
const PARTIAL_SUCCESS_MESSAGE =
  "Some clients were successfully imported, but we encountered issues with a few clients. Please check the details and upload again.";
const FAILURE_MESSAGE =
  "We encountered some issues while processing your document. Below, you’ll find details about what went wrong. Please fix the errors and try again.";
const FULL_SUCCESS_MESSAGE = "Clients uploaded successfully.";
const UNKOWN_ERROR_MESSAGE =
  "An error occurred while uploading clients. Please wait a few minutes and try again. If this keeps occurring please contact support@smartwiz.io";

export const useBulkUpload = () => {
  const queryClient = useQueryClient();
  const [csv, setCsv] = useState<File | null>(null);
  const [notification, setNotification] = useState<{
    title?: string;
    message: string;
    type: BulkClientAlertType;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  /** Errors occurring during file processing */
  const [failedRecords, setFailedRecords] = useState<
    BulkClientUploadRecordError[]
  >([]);
  const [existingRecords, setExistingRecords] = useState<string[]>([]);
  const [fileErrors, setFileErrors] = useState<BulkClientUploadFileError[]>([]);

  /**
   * Clears the state of the modal.
   */
  const resetState = (clearFile: boolean = true) => {
    setNotification(null);
    setLoading(false);
    setFailedRecords([]);
    setExistingRecords([]);
    setFileErrors([]);
    if (clearFile) setCsv(null);
  };

  /**
   * Attempts to upload CSV file and handles server response.
   * @param e
   */
  const handleBulkUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!csv) {
      setNotification({
        type: "error",
        message: NO_FILE_SELECTED_MESSAGE,
      });
      return;
    }

    // Starting request
    resetState(false);
    setNotification({
      title: UPLOADING_TITLE,
      message: UPLOADING_MESSAGE,
      type: "info",
    });
    setLoading(true);

    return bulkUpload(csv)
      .then((data) => {
        setFailedRecords(data.failed);
        setExistingRecords(data.existing);
        setFileErrors(data.fileErrors);
        const [type, message] = getNotificationDetails(data);
        setNotification({ type, message });
        queryClient.invalidateQueries({ queryKey: ["clients"] });
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        resetState();
        setNotification({
          message: UNKOWN_ERROR_MESSAGE,
          type: "error",
        });
      });
  };

  return {
    csv,
    setCsv,
    loading,
    notification,
    errorModalOpen,
    failedRecords,
    existingRecords,
    setErrorModalOpen,
    handleBulkUpload,
    resetState,
    fileErrors,
  };
};

/**
 * Calculates what the notification should be based on the response from the server.
 * @param res Bulk Client Upload Response.
 */
const getNotificationDetails = (
  res: BulkClientUploadResponse
): [BulkClientAlertType, string] => {
  const hasError = Boolean(res.failed.length || res.fileErrors.length);
  if (hasError) {
    if (res.created.length) return ["warning", PARTIAL_SUCCESS_MESSAGE];
    else return ["error", FAILURE_MESSAGE];
  } else return ["success", FULL_SUCCESS_MESSAGE];
};
